import * as distance from '~/constants/distance';
import {
  POLYMAILER_DISTANCE_CM,
  POLYMAILER_DISTANCE_CM_VALUE,
  POLYMAILER_DISTANCE_IN,
  POLYMAILER_DISTANCE_IN_VALUE,
  POLYMAILER_DISTANCE_MM,
  POLYMAILER_DISTANCE_MM_VALUE,
} from '~/constants/polyMailerValues';
import * as weight from '~/constants/weight';
import CarrierParcelTemplate, * as carrierParcelTemplatefrom from '~/types/CarrierParcelTemplate';

interface BaseJSONParcelTemplate {
  object_id: string;
  object_created?: string;
  object_updated?: string;
  is_default: boolean;
  name: string;
  distance_unit: string;
  height: Maybe<string>;
  length: Maybe<string>;
  width: string;
  weight_unit: string;
  weight: string;
}

interface JSONParcelTemplate extends BaseJSONParcelTemplate {
  template: Maybe<carrierParcelTemplatefrom.JSONCarrierParcelTemplate>;
}

interface SavedJSONParcelTemplate extends BaseJSONParcelTemplate {
  template: string;
}

type ParcelTemplate = {
  objectId?: string;
  objectCreated?: Date;
  objectUpdated?: Date;
  carrierTemplate: Maybe<CarrierParcelTemplate>;
  isDefault: boolean;
  name: string;
  distanceUnit: distance.DistanceUnit;
  height: number;
  length: number;
  width: number;
  weightUnit: weight.WeightUnit;
  weight: number;
};

export type WebUserParcelTemplate = {
  distance_unit: distance.DistanceUnit;
  height: string;
  is_default: boolean;
  length: string;
  name: string;
  object_id?: string;
  template?: carrierParcelTemplatefrom.WebCarrierParcelTemplate;
  weight: string;
  weight_unit: weight.WeightUnit;
  width: string;
};

const unitValue: ParcelTemplate = {
  objectId: undefined,
  carrierTemplate: undefined,
  isDefault: false,
  name: '',
  distanceUnit: distance.DistanceUnit.IN,
  height: 0,
  length: 0,
  width: 0,
  weightUnit: weight.WeightUnit.LB,
  weight: 0,
};

function fromJSON(jsonParcelTemplate: JSONParcelTemplate): Maybe<ParcelTemplate> {
  if (!jsonParcelTemplate) return null;

  const result: ParcelTemplate = {
    objectId: jsonParcelTemplate.object_id,
    carrierTemplate: carrierParcelTemplatefrom.fromJSON(jsonParcelTemplate.template),
    isDefault: jsonParcelTemplate.is_default,
    name: jsonParcelTemplate.name,
    distanceUnit: distance.distanceUnitMap[jsonParcelTemplate.distance_unit],
    height: Number(jsonParcelTemplate.height),
    length: Number(jsonParcelTemplate.length),
    width: Number(jsonParcelTemplate.width),
    weightUnit: weight.weightUnitMap[jsonParcelTemplate.weight_unit],
    weight: Number(jsonParcelTemplate.weight),
  };

  const { object_created, object_updated } = jsonParcelTemplate;
  if (object_created) {
    result.objectCreated = new Date(object_created);
  }
  if (object_updated) {
    result.objectUpdated = new Date(object_updated);
  }

  return result;
}

/*
 * This was added as conservative check, somewhere up the chain
 * when we are creating the ParcelTemplate object we use object spread (...)
 * operator and it somehow converts date in string.
 *
 * This cannot be simulated in the typescript environment, only in compiled environment
 *
 * We don't really care for the dates, so we just pass empty if its a object
 */
export function dateISOString(date?: Date): string {
  if (typeof date === 'object' && typeof date.toISOString === 'function') {
    return date.toISOString();
  }

  return '';
}

function toJSON(parcelTemplate: ParcelTemplate): SavedJSONParcelTemplate {
  const jsonParcelTemplate: SavedJSONParcelTemplate = {
    object_id: '',
    object_created: dateISOString(parcelTemplate.objectCreated),
    object_updated: dateISOString(parcelTemplate.objectUpdated),
    template: '',
    is_default: parcelTemplate.isDefault,
    name: parcelTemplate.name,
    distance_unit: parcelTemplate.distanceUnit,
    height: parcelTemplate.height.toString(),
    length: parcelTemplate.length.toString(),
    width: parcelTemplate.width.toString(),
    weight_unit: parcelTemplate.weightUnit,
    weight: parcelTemplate.weight.toString(),
  };

  if (parcelTemplate.objectId) {
    jsonParcelTemplate.object_id = parcelTemplate.objectId;
  }
  if (parcelTemplate.carrierTemplate) {
    jsonParcelTemplate.template = parcelTemplate.carrierTemplate.token;
  }
  return jsonParcelTemplate;
}

export const fromWebUserParcelTemplate = (template?: WebUserParcelTemplate): ParcelTemplate => {
  if (!template) {
    return unitValue;
  }

  const carrierTemplate = carrierParcelTemplatefrom.fromWebCarrierParcelTemplate(template.template);
  return {
    objectId: template.object_id,
    isDefault: template.is_default,
    name: template.name,
    distanceUnit: template.distance_unit,
    height: Number(template.height),
    length: Number(template.length),
    width: Number(template.width),
    weightUnit: template.weight_unit,
    weight: Number(template.weight),
    carrierTemplate,
  };
};

type DimensionProvider = {
  length: number;
  width: number;
  height: number;
  distanceUnit: distance.DistanceUnit;
};

function getDimensionsString(dimensionProvider: DimensionProvider): string {
  if (
    dimensionProvider.length === 0 &&
    dimensionProvider.width === 0 &&
    dimensionProvider.height === 0
  ) {
    // softpack carrier template
    return '';
  }

  // get polymailer heights for selected unit
  const selectedPolymailerHeightInches =
    dimensionProvider?.distanceUnit === POLYMAILER_DISTANCE_IN &&
    dimensionProvider?.height === POLYMAILER_DISTANCE_IN_VALUE
      ? true
      : false;
  const selectedPolymailerHeightCm =
    dimensionProvider?.distanceUnit === POLYMAILER_DISTANCE_CM &&
    dimensionProvider?.height === POLYMAILER_DISTANCE_CM_VALUE
      ? true
      : false;
  const selectedPolymailerHeightMm =
    dimensionProvider?.distanceUnit === POLYMAILER_DISTANCE_MM &&
    dimensionProvider?.height === POLYMAILER_DISTANCE_MM_VALUE
      ? true
      : false;

  if (dimensionProvider.length && dimensionProvider.width && dimensionProvider.height === 0) {
    // user parcel template from softpack
    return `${dimensionProvider.length} x ${dimensionProvider.width} ${dimensionProvider.distanceUnit}`;
  }

  if (
    selectedPolymailerHeightInches ||
    selectedPolymailerHeightCm ||
    (selectedPolymailerHeightMm && dimensionProvider.length && dimensionProvider.width)
  ) {
    return `${dimensionProvider.length} x ${dimensionProvider.width} ${dimensionProvider.distanceUnit}`;
  }

  return `${dimensionProvider.length} x ${dimensionProvider.width} x ${dimensionProvider.height} ${dimensionProvider.distanceUnit}`;
}

type WeightProvider = {
  weight: Maybe<string>;
  weightUnit: Maybe<string>;
};

function getWeightString(weightProvider: ParcelTemplate | WeightProvider): string {
  return weightProvider.weight ? `${weightProvider.weight} ${weightProvider.weightUnit}` : '';
}

function getName(parcelTemplate: ParcelTemplate): string {
  return parcelTemplate.carrierTemplate ? parcelTemplate.carrierTemplate.name : parcelTemplate.name;
}

/**
 * Confirms whether parcel template is of type `ParcelTemplate`.
 *
 * For more info on the type guard pattern, see https://www.typescriptlang.org/docs/handbook/advanced-types.html#user-defined-type-guards
 *
 * @param parcelTemplate parcel template to type-check
 */
function isParcelTemplate(
  parcelTemplate: ParcelTemplate | CarrierParcelTemplate,
): parcelTemplate is ParcelTemplate {
  return (<ParcelTemplate>parcelTemplate).carrierTemplate !== undefined;
}

export default ParcelTemplate;

export {
  CarrierParcelTemplate,
  JSONParcelTemplate,
  SavedJSONParcelTemplate,
  fromJSON,
  toJSON,
  getDimensionsString,
  getWeightString,
  getName,
  isParcelTemplate,
};
